import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SITE_NAME, buildPageTitle } from '../data/site';
import { getWhatsAppPhone } from '../utils/whatsapp';

interface SEOProps {
  title: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

export const SEO: React.FC<SEOProps> = ({ 
  title, 
  description = "Líder em aluguel de som, iluminação, painel de LED e equipamentos audiovisuais em Belo Horizonte e região.",
  keywords = "aluguel de som, iluminação, painel de led, eventos, belo horizonte",
  image = "https://www.xproducoeseeventos.com.br/xproducoes-logo.png",
  url = typeof window !== 'undefined' ? window.location.href : '',
  type = 'website',
  jsonLd
}) => {
  const siteTitle = SITE_NAME;
  // Achado: páginas que já passavam "… | X-Produções" ganhavam o sufixo duas vezes
  // ("Comparar Itens | X-Produções | X Produções"). buildPageTitle não duplica.
  const fullTitle = buildPageTitle(title);
  const safeUrl = typeof url === 'string' ? url : '';
  
  // Canonical URL logic: remove query params to avoid duplicate content issues
  const canonicalUrl = safeUrl.split('?')[0];

  // Schema.org Local Business data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "X Produções",
    "image": [image],
    "@id": "https://www.xproducoeseeventos.com.br",
    "url": "https://www.xproducoeseeventos.com.br",
    // Achado: era +5531998522380, número diferente do WhatsApp mostrado no site. Agora sai da
    // mesma fonte do botão de WhatsApp (VITE_WHATSAPP_PHONE, padrão (31) 98925-2272).
    "telephone": `+${getWhatsAppPhone()}`,
    "priceRange": "$$",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Belo Horizonte",
      "addressRegion": "MG",
      "addressCountry": "BR"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": -19.9208, // BH Coordinates placeholder
      "longitude": -43.9378
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday"
      ],
      "opens": "09:00",
      "closes": "18:00"
    }
  };

  // Logic to build a hybrid schema array (LocalBusiness + custom jsonLd from page)
  const jsonLdArray = Array.isArray(jsonLd) 
    ? [structuredData, ...jsonLd] 
    : jsonLd 
        ? [structuredData, jsonLd] 
        : [structuredData];

  return (
    <Helmet>
      {/* Standard Metadata */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={siteTitle} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={safeUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Structured Data (JSON-LD) Array-Based for rich hybrid snippets */}
      <script type="application/ld+json">
        {JSON.stringify(jsonLdArray)}
      </script>
    </Helmet>
  );
};
